import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Car,
  Calculator,
  ChevronRight,
  MapPin,
  Phone,
  Zap,
} from "lucide-react";
import PasswordModal from "./PasswordModal";

const PanelSelector = ({ onPanelSelected }) => {
  const [showDealershipModal, setShowDealershipModal] = useState(false);
  const [showAccountingModal, setShowAccountingModal] = useState(false);

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center p-6"
      style={{
        background: "linear-gradient(135deg, hsl(var(--background)) 0%, hsl(142 30% 96%) 100%)",
      }}
    >
      {/* Header */}
      <div className="text-center mb-10 space-y-3">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 rounded-2xl bg-primary/10">
            <Zap className="h-8 w-8 text-primary" />
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_384f5a02-e73c-441b-8711-7b4aa811af91/artifacts/1llnvk1o_be6ba909-56b0-4515-9de1-7b59366d1e68_Untitled.avif"
            alt="Kinetic Green Logo"
            className="h-16 w-auto object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
          SHREE MOBILITY TRADE
        </h1>
        <p className="text-muted-foreground text-base max-w-md mx-auto">
          Authorised Kinetic Green EV Dealer — Rampurhat, Birbhum, West Bengal
        </p>
        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4 text-primary" />
            NH 14, Brahmanigram More, Rampurhat
          </span>
          <span className="flex items-center gap-1.5">
            <Phone className="h-4 w-4 text-primary" />
            9749430705 / 7063511705
          </span>
        </div>
      </div>

      {/* Panel Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
        {/* Dealership Panel */}
        <button
          onClick={() => setShowDealershipModal(true)}
          className="text-left group focus:outline-none"
          id="dealership-panel-btn"
        >
          <Card className="border-border h-full transition-all duration-300 hover:shadow-xl hover:border-primary/50 group-hover:-translate-y-1 cursor-pointer relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardContent className="pt-8 pb-8 px-6 flex flex-col gap-4">
              <div className="flex items-start justify-between">
                <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Car className="h-7 w-7 text-primary" />
                </div>
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                  Active
                </Badge>
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground mb-2">
                  Dealership Panel
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Create quotations, tax invoices, and service bills. Manage vehicle sales records and print documents.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-primary text-sm font-medium mt-auto pt-2">
                <span>Click to access</span>
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </button>

        {/* Accounting Panel */}
        <button
          onClick={() => setShowAccountingModal(true)}
          className="text-left group focus:outline-none"
          id="accounting-panel-btn"
        >
          <Card className="border-border h-full transition-all duration-300 hover:shadow-xl hover:border-blue-500/50 group-hover:-translate-y-1 cursor-pointer relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardContent className="pt-8 pb-8 px-6 flex flex-col gap-4">
              <div className="flex items-start justify-between">
                <div className="p-3 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                  <Calculator className="h-7 w-7 text-blue-600" />
                </div>
                <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-600">
                  Restricted
                </Badge>
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground mb-2">
                  Accounting Panel
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  View pending entries, financial summaries, and records submitted by the dealership team.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-blue-600 text-sm font-medium mt-auto pt-2">
                <span>Click to access</span>
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </button>
      </div>

      <p className="text-xs text-muted-foreground mt-10 text-center">
        © 2025 SHREE MOBILITY TRADE. All rights reserved.
      </p>

      {/* Dealership Password Modal */}
      <PasswordModal
        open={showDealershipModal}
        onClose={() => setShowDealershipModal(false)}
        onSuccess={onPanelSelected}
        panelType="dealership"
      />

      {/* Accounting Password Modal */}
      <PasswordModal
        open={showAccountingModal}
        onClose={() => setShowAccountingModal(false)}
        onSuccess={onPanelSelected}
        panelType="accounting"
      />
    </div>
  );
};

export default PanelSelector;
